// @spec AUD-STR-037
// Evidence detail page route

import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Download, ExternalLink, FileText, RefreshCw } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useEvidenceDetail } from '@/features/evidence/api/use-evidence-detail';
import { ControlVerificationPanel } from '@/features/evidence/components/ControlVerificationPanel';
import type { EvidenceResponse } from '@/features/evidence/types';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_authenticated/audit/evidence/$evidenceId')({
  component: EvidenceDetailPage,
});

type HubStatus = 'valid' | 'pending_review' | 'expired';
type HubEvidenceType = 'document' | 'screenshot' | 'log' | 'certificate' | 'system_export';
type LinkedObjectType =
  | 'asset'
  | 'risk'
  | 'vulnerability'
  | 'training_record'
  | 'awareness_campaign'
  | 'policy'
  | 'control';

interface LinkedObjectRef {
  type: LinkedObjectType;
  id: string | null;
  label: string;
  href: string | null;
}

interface EvidenceVersionItem {
  version: string;
  updatedAt: string | null;
  updatedBy: string | null;
  fileName: string | null;
  notes: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function normalizeDate(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

function formatDate(value: string | null): string {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatFileSize(bytes: number | null): string {
  if (!bytes || bytes <= 0) {
    return '-';
  }
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${String(Math.round(bytes / 1024))} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function getFileExtension(fileName: string | null): string {
  if (!fileName) {
    return '';
  }
  const split = fileName.toLowerCase().split('.');
  return split.length > 1 ? (split[split.length - 1] ?? '') : '';
}

function getLinkedObjectHref(type: LinkedObjectType, id: string | null): string | null {
  if (type === 'asset' && id) return `/assets/${id}`;
  if (type === 'risk' && id) return `/risks/${id}`;
  if (type === 'vulnerability' && id) return `/vulnerabilities/${id}`;
  if (type === 'policy' && id) return `/policies/${id}`;
  if (type === 'training_record' && id) return `/workforce-readiness/training-register/${id}`;
  if (type === 'awareness_campaign' && id)
    return `/workforce-readiness/awareness-campaigns/${id}`;
  if (type === 'control') return '/controls';
  return null;
}

function parseLinkedObjectType(value: string | null): LinkedObjectType | null {
  if (!value) {
    return null;
  }
  const normalized = value.trim().toLowerCase();
  if (
    normalized === 'asset' ||
    normalized === 'risk' ||
    normalized === 'vulnerability' ||
    normalized === 'training' ||
    normalized === 'training_record' ||
    normalized === 'awareness_campaign' ||
    normalized === 'policy' ||
    normalized === 'control'
  ) {
    return normalized === 'training' ? 'training_record' : normalized;
  }
  return null;
}

function parseLinkedObjects(evidence: EvidenceResponse): LinkedObjectRef[] {
  const refs: LinkedObjectRef[] = [];
  const dedupe = new Set<string>();

  const pushRef = (typeValue: string | null, id: string | null, label: string | null) => {
    const type = parseLinkedObjectType(typeValue);
    if (!type) {
      return;
    }
    const trimmedId = id?.trim();
    const safeId = trimmedId && trimmedId.length > 0 ? trimmedId : null;
    const trimmedLabel = label?.trim();
    const safeLabel =
      trimmedLabel && trimmedLabel.length > 0
        ? trimmedLabel
        : `${type}${safeId ? ` ${safeId}` : ''}`;
    const key = `${type}:${safeId ?? ''}:${safeLabel.toLowerCase()}`;
    if (dedupe.has(key)) {
      return;
    }
    dedupe.add(key);
    refs.push({
      type,
      id: safeId,
      label: safeLabel,
      href: getLinkedObjectHref(type, safeId),
    });
  };

  const metadata = isRecord(evidence.metadata) ? evidence.metadata : null;

  if (metadata) {
    const linkedObjectsRaw = metadata.linkedObjects;
    if (Array.isArray(linkedObjectsRaw)) {
      for (const rawEntry of linkedObjectsRaw) {
        if (!isRecord(rawEntry)) {
          continue;
        }
        pushRef(
          readString(rawEntry, 'type'),
          readString(rawEntry, 'id'),
          readString(rawEntry, 'name') ?? readString(rawEntry, 'title')
        );
      }
    }

    const sourceRecord = metadata.sourceRecord;
    if (isRecord(sourceRecord)) {
      pushRef(
        readString(sourceRecord, 'type'),
        readString(sourceRecord, 'id'),
        readString(sourceRecord, 'name') ?? readString(sourceRecord, 'title')
      );
    }

    pushRef(
      readString(metadata, 'linkedObjectType'),
      readString(metadata, 'linkedObjectId'),
      readString(metadata, 'linkedObjectName')
    );
  }

  for (const tag of evidence.tags) {
    const linkedTagMatch =
      /^linked_object:(asset|risk|vulnerability|training|training_record|awareness_campaign|policy|control):(.+)$/i.exec(tag);
    if (linkedTagMatch) {
      pushRef(linkedTagMatch[1] ?? null, linkedTagMatch[2] ?? null, null);
      continue;
    }

    const directTagMatch =
      /^(asset|risk|vulnerability|training|training_record|awareness_campaign|policy|control):(.+)$/i.exec(
        tag
      );
    if (directTagMatch) {
      pushRef(directTagMatch[1] ?? null, directTagMatch[2] ?? null, null);
    }
  }

  return refs;
}

function getExpiryDate(evidence: EvidenceResponse): string | null {
  const metadata = isRecord(evidence.metadata) ? evidence.metadata : null;
  if (!metadata) {
    return null;
  }
  return (
    normalizeDate(readString(metadata, 'expiryDate')) ??
    normalizeDate(readString(metadata, 'expiry_date')) ??
    normalizeDate(readString(metadata, 'expiresAt')) ??
    normalizeDate(readString(metadata, 'expires_at'))
  );
}

function isExpired(expiryDate: string | null): boolean {
  if (!expiryDate) {
    return false;
  }
  const parsed = new Date(expiryDate);
  if (Number.isNaN(parsed.getTime())) {
    return false;
  }
  return parsed.getTime() < Date.now();
}

function mapHubStatus(evidence: EvidenceResponse, expiryDate: string | null): HubStatus {
  if (isExpired(expiryDate)) {
    return 'expired';
  }
  if (evidence.status === 'approved') {
    return 'valid';
  }
  return 'pending_review';
}

function mapHubType(evidence: EvidenceResponse): HubEvidenceType {
  if (evidence.evidenceType === 'screenshot') {
    return 'screenshot';
  }

  const extension = getFileExtension(evidence.fileName);
  const tags = new Set(evidence.tags.map((tag) => tag.toLowerCase()));

  if (
    tags.has('certificate') ||
    tags.has('cert') ||
    extension === 'cer' ||
    extension === 'crt' ||
    extension === 'pem'
  ) {
    return 'certificate';
  }

  if (
    tags.has('system_export') ||
    tags.has('export') ||
    tags.has('system-export') ||
    extension === 'csv' ||
    extension === 'json' ||
    extension === 'xml' ||
    extension === 'zip'
  ) {
    return 'system_export';
  }

  if (tags.has('log') || extension === 'log' || extension === 'txt') {
    return 'log';
  }

  return 'document';
}

const STATUS_LABEL_KEYS: Record<HubStatus, string> = {
  pending_review: 'auditDetail.statusLabels.pendingReview',
  expired: 'auditDetail.statusLabels.expired',
  valid: 'auditDetail.statusLabels.valid',
};

function getStatusBadgeClass(status: HubStatus): string {
  if (status === 'valid') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  }
  if (status === 'expired') {
    return 'border-red-200 bg-red-50 text-red-700';
  }
  return 'border-amber-200 bg-amber-50 text-amber-700';
}

const TYPE_LABEL_KEYS: Record<HubEvidenceType, string> = {
  document: 'auditDetail.typeLabels.document',
  screenshot: 'auditDetail.typeLabels.screenshot',
  log: 'auditDetail.typeLabels.log',
  certificate: 'auditDetail.typeLabels.certificate',
  system_export: 'auditDetail.typeLabels.systemExport',
};

function parseVersionHistory(evidence: EvidenceResponse): EvidenceVersionItem[] {
  const metadata = isRecord(evidence.metadata) ? evidence.metadata : null;
  const historyRaw = metadata?.versionHistory;
  if (Array.isArray(historyRaw)) {
    const parsed: EvidenceVersionItem[] = [];
    for (const entry of historyRaw) {
      if (!isRecord(entry)) {
        continue;
      }
      parsed.push({
        version: readString(entry, 'version') ?? `v${String(parsed.length + 1)}`,
        updatedAt: normalizeDate(readString(entry, 'updatedAt') ?? readString(entry, 'updated_at')),
        updatedBy: readString(entry, 'updatedBy') ?? readString(entry, 'updated_by'),
        fileName: readString(entry, 'fileName') ?? readString(entry, 'file_name'),
        notes: readString(entry, 'notes'),
      });
    }
    if (parsed.length > 0) {
      return parsed;
    }
  }

  return [
    {
      version: 'v1',
      updatedAt: normalizeDate(evidence.updatedAt),
      updatedBy: evidence.submittedBy?.displayName ?? null,
      fileName: evidence.fileName,
      notes: null,
    },
  ];
}

function renderLinkedItems(items: LinkedObjectRef[], emptyLabel: string) {
  if (items.length === 0) {
    return <span className="text-sm text-muted-foreground">{emptyLabel}</span>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((item) => (
        <a
          key={`${item.type}:${item.id ?? item.label}`}
          href={item.href ?? '#'}
          className={cn(
            'inline-flex items-center gap-1 rounded border px-2 py-1 text-xs',
            item.href
              ? 'border-primary/30 text-primary hover:bg-primary/5'
              : 'border-border text-muted-foreground'
          )}
          onClick={(event) => {
            if (!item.href) {
              event.preventDefault();
            }
          }}
        >
          <span className="capitalize">{item.type}</span>
          <ExternalLink className="h-3 w-3" />
        </a>
      ))}
    </div>
  );
}

function EvidenceDetailPage() {
  const { t } = useTranslation('evidence');
  const { evidenceId } = Route.useParams();
  const { evidence, isLoading, isError, error, refetch } = useEvidenceDetail(evidenceId);

  if (isLoading) {
    return <EvidenceDetailSkeleton />;
  }

  if (isError || !evidence) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-6xl">
          <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-6 text-center">
            <h2 className="text-lg font-semibold text-destructive">{t('auditDetail.notFound')}</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {error?.message ?? t('auditDetail.notFoundDesc')}
            </p>
            <div className="mt-4 flex items-center justify-center gap-2">
              <Button variant="outline" onClick={refetch}>
                <RefreshCw className="mr-2 h-4 w-4" />
                {t('auditDetail.retry')}
              </Button>
              <Button asChild>
                <Link to="/audit/evidence-library">{t('auditDetail.backToLibrary')}</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const expiryDate = getExpiryDate(evidence);
  const hubStatus = mapHubStatus(evidence, expiryDate);
  const hubType = mapHubType(evidence);
  const linkedObjects = parseLinkedObjects(evidence);
  const versions = parseVersionHistory(evidence);

  const metadata = isRecord(evidence.metadata) ? evidence.metadata : null;
  const verificationMethodRaw = (
    readString(metadata ?? {}, 'verificationMethod') ?? 'manual'
  ).toLowerCase();
  const verificationMethod = verificationMethodRaw === 'automated' ? 'Automated' : 'Manual';
  const verificationDate =
    normalizeDate(evidence.reviewedAt ?? null) ??
    normalizeDate(
      readString(metadata ?? {}, 'verificationDate') ?? readString(metadata ?? {}, 'verifiedAt')
    );
  const verifiedBy =
    evidence.reviewedBy?.displayName ??
    readString(metadata ?? {}, 'verifiedBy') ??
    readString(metadata ?? {}, 'verifiedByName');
  const auditorNotes =
    evidence.reviewNotes ??
    readString(metadata ?? {}, 'auditorNotes') ??
    readString(metadata ?? {}, 'verificationNotes');

  const linkedPolicies = linkedObjects.filter((item) => item.type === 'policy');
  const linkedAssetsRisksVulns = linkedObjects.filter(
    (item) =>
      item.type === 'asset' ||
      item.type === 'risk' ||
      item.type === 'vulnerability' ||
      item.type === 'training'
  );

  const downloadHref =
    evidence.downloadUrl ?? (evidence.fileId ? `/api/v1/files/${evidence.fileId}/download` : null);

  return (
    <div className="p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2">
              <Link to="/audit/evidence-library">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('auditDetail.backToLibrary')}
              </Link>
            </Button>
            <h1 className="text-2xl font-semibold">{evidence.title}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('auditDetail.evidenceId')}: <span className="font-mono">{evidence.id}</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={cn('border', getStatusBadgeClass(hubStatus))}>
              {t(STATUS_LABEL_KEYS[hubStatus])}
            </Badge>
            <Badge variant="outline">{t(TYPE_LABEL_KEYS[hubType])}</Badge>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>{t('auditDetail.basicInfo.title')}</CardTitle>
                <CardDescription>{t('auditDetail.basicInfo.description')}</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs text-muted-foreground">{t('auditDetail.basicInfo.evidenceName')}</p>
                  <p className="mt-1 text-sm font-medium">{evidence.title}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('auditDetail.basicInfo.owner')}</p>
                  <p className="mt-1 text-sm">{evidence.submittedBy?.displayName ?? '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('auditDetail.basicInfo.evidenceType')}</p>
                  <p className="mt-1 text-sm">{t(TYPE_LABEL_KEYS[hubType])}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('auditDetail.basicInfo.status')}</p>
                  <p className="mt-1 text-sm">{t(STATUS_LABEL_KEYS[hubStatus])}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-xs text-muted-foreground">{t('auditDetail.basicInfo.descriptionLabel')}</p>
                  <p className="mt-1 text-sm">{evidence.description ?? '-'}</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('auditDetail.links.title')}</CardTitle>
                <CardDescription>
                  {t('auditDetail.links.description')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-xs text-muted-foreground">{t('auditDetail.links.linkedControl')}</p>
                  <p className="mt-1 text-sm">{evidence.complianceRequirement ?? t('auditDetail.links.notLinked')}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('auditDetail.links.linkedRegulation')}</p>
                  <p className="mt-1 text-sm">{evidence.complianceDomain ?? t('auditDetail.links.notLinked')}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('auditDetail.links.linkedPolicy')}</p>
                  <div className="mt-1">{renderLinkedItems(linkedPolicies, t('auditDetail.links.notLinked'))}</div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">
                    {t('auditDetail.links.linkedAssetRiskVulnTraining')}
                  </p>
                  <div className="mt-1">
                    {renderLinkedItems(linkedAssetsRisksVulns, t('auditDetail.links.notLinked'))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('auditDetail.filesVersions.title')}</CardTitle>
                <CardDescription>{t('auditDetail.filesVersions.description')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3">
                  <div>
                    <p className="text-sm font-medium">{evidence.fileName ?? t('auditDetail.filesVersions.noFileAttached')}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(evidence.fileSizeBytes)}
                      {evidence.fileMimeType ? ` • ${evidence.fileMimeType}` : ''}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t('auditDetail.filesVersions.lastUpdated')}: {formatDate(evidence.updatedAt)}
                    </p>
                  </div>
                  {downloadHref && (
                    <Button asChild variant="outline" size="sm">
                      <a href={downloadHref}>
                        <Download className="mr-2 h-4 w-4" />
                        {t('auditDetail.filesVersions.download')}
                      </a>
                    </Button>
                  )}
                </div>

                <div>
                  <p className="mb-2 text-xs text-muted-foreground">{t('auditDetail.filesVersions.versionHistory')}</p>
                  <div className="overflow-x-auto rounded-lg border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('auditDetail.filesVersions.version')}</TableHead>
                          <TableHead>{t('auditDetail.filesVersions.updatedAt')}</TableHead>
                          <TableHead>{t('auditDetail.filesVersions.updatedBy')}</TableHead>
                          <TableHead>{t('auditDetail.filesVersions.file')}</TableHead>
                          <TableHead>{t('auditDetail.filesVersions.notes')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {versions.map((version, index) => (
                          <TableRow key={`${version.version}-${String(index)}`}>
                            <TableCell>{version.version}</TableCell>
                            <TableCell>{formatDate(version.updatedAt)}</TableCell>
                            <TableCell>{version.updatedBy ?? '-'}</TableCell>
                            <TableCell>{version.fileName ?? '-'}</TableCell>
                            <TableCell>{version.notes ?? '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('auditDetail.verification.title')}</CardTitle>
                <CardDescription>{t('auditDetail.verification.description')}</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs text-muted-foreground">{t('auditDetail.verification.method')}</p>
                  <p className="mt-1 text-sm">{verificationMethod}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('auditDetail.verification.verifiedBy')}</p>
                  <p className="mt-1 text-sm">{verifiedBy ?? '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('auditDetail.verification.verificationDate')}</p>
                  <p className="mt-1 text-sm">{formatDate(verificationDate)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('auditDetail.verification.expiryDate')}</p>
                  <p className="mt-1 text-sm">{formatDate(expiryDate)}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-xs text-muted-foreground">{t('auditDetail.verification.auditorNotes')}</p>
                  <p className="mt-1 text-sm">{auditorNotes ?? '-'}</p>
                </div>
              </CardContent>
            </Card>

            <ControlVerificationPanel evidenceId={evidence.id} isAuditor />
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('auditDetail.metadata.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground">{t('auditDetail.metadata.uploadDate')}</p>
                  <p className="mt-1 text-sm">{formatDate(evidence.submittedAt)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('auditDetail.metadata.createdAt')}</p>
                  <p className="mt-1 text-sm">{formatDate(evidence.createdAt)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('auditDetail.metadata.lastUpdated')}</p>
                  <p className="mt-1 text-sm">{formatDate(evidence.updatedAt)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('auditDetail.metadata.tags')}</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {evidence.tags.length === 0 && (
                      <span className="text-sm text-muted-foreground">-</span>
                    )}
                    {evidence.tags.map((tag) => (
                      <Badge key={tag} variant="outline" className="text-[10px]">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
                {evidence.fileHash && (
                  <div>
                    <p className="text-xs text-muted-foreground">{t('auditDetail.metadata.fileHash')}</p>
                    <p className="mt-1 break-all font-mono text-xs text-muted-foreground">
                      {evidence.fileHash}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('auditDetail.quickLinks.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button asChild variant="outline" className="w-full justify-start">
                  <Link to="/audit/evidence-library">
                    <FileText className="mr-2 h-4 w-4" />
                    {t('auditDetail.quickLinks.evidenceLibrary')}
                  </Link>
                </Button>
                {linkedObjects.slice(0, 5).map((item) => (
                  <Button
                    key={`quick-${item.type}-${item.id ?? item.label}`}
                    asChild
                    variant="ghost"
                    className="w-full justify-start"
                    disabled={!item.href}
                  >
                    <a href={item.href ?? '#'}>
                      <span className="mr-2 capitalize">{item.type}</span>
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

function EvidenceDetailSkeleton() {
  return (
    <div className="p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-56" />
          <Skeleton className="h-4 w-80" />
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            {Array.from({ length: 4 }).map((_, idx) => (
              <Card key={idx}>
                <CardHeader>
                  <Skeleton className="h-6 w-44" />
                </CardHeader>
                <CardContent className="space-y-3">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-4 w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-28" />
              </CardHeader>
              <CardContent className="space-y-3">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-2/3" />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
